import {
  CaretDownOutlined,
  CaretUpOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { ShareSelectInput } from '@components'
import {
  EEventPostType,
  EEventPostTypeLabel,
  PATH_ADD_EVENT_NEWS,
  PATH_EVENT_POST,
} from '@configs'
import { RootState, selectEventPostsLoading, useAppDispatch } from '@redux'
import { Skeleton, Tooltip, Typography, message } from 'antd'
import { t } from 'i18next'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button, Input, Loading, SharedTable } from 'src/common'
import ConfirmDeleteModal from 'src/components/staff-and-role-management/ConfirmDeleteModal'
import { useDebounce } from 'src/hooks/useDebounce'
import { IEventPostParams } from 'src/interfaces/event-posts'
import {
  deleteEventPostAction,
  getEventPostDetailAction,
  getListEventPostsAction,
} from 'src/redux/actions/event-posts'
import styled from 'styled-components'

// Filter options for event types only
const eventTypeOptions: { value: string; label: string }[] = [
  {
    value: 'all',
    label: 'All Events',
  },
  {
    value: EEventPostType.EVENT_POST_NEWS,
    label: EEventPostTypeLabel.EVENT_POST_NEWS,
  },
  {
    value: EEventPostType.EVENT_POST_RECAP,
    label: EEventPostTypeLabel.EVENT_POST_RECAP,
  },
]

const eventStatusOptions: { value: string; label: string }[] = [
  {
    value: 'all status',
    label: 'All status',
  },
  {
    value: 'published',
    label: 'Published',
  },
  {
    value: 'unpublished',
    label: 'Unpublished',
  },
]

const enumTableTitleEvents = {
  NO: 'No',
  TITLE: 'Title',
  TYPE: 'Type',
  LOCATION: 'Location',
  CREATE_TIME: 'Create time',
  STATUS: 'Status',
  ACTION: 'Action',
}

const EventNewsPage = () => {
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false)
  const [searchValue, setSearchValue] = useState<string | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const debouncedValue = useDebounce<string | null>(searchValue, 800)
  const [sort, setSort] = useState<{ [key: string]: any }>({})
  const [status, setStatus] = useState(eventStatusOptions[0].label)
  const [eventType, setEventType] = useState(eventTypeOptions[0].value)
  const [selectedEventId, setSelectedEventId] = useState<number | string>()
  const [isModalLoading, setIsModalLoading] = useState(false)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Get event posts directly from event posts state
  const { eventPosts, totalEventPosts, pageEventPosts, limitEventPosts } =
    useSelector((state: RootState) => state.eventPosts)

  const getEvents = async (params?: IEventPostParams) => {
    try {
      setIsSearching(true)
      const eventParams: IEventPostParams = {
        page: params?.page || 1,
        limit: params?.limit || +limitEventPosts,
        search: params?.search,
        sort: params?.sort || sort,
        ...params,
      }

      // Cast to any to allow type property
      const eventParamsWithType = eventParams as any

      const response = await dispatch(
        getListEventPostsAction(eventParamsWithType)
      ).unwrap()

      // Check if current page is empty and there are items on previous pages
      const responseData = response?.data as any
      if (
        responseData?.items?.length === 0 &&
        responseData?.page > 1 &&
        responseData?.total > 0
      ) {
        // Reload with previous page
        const prevPageParams = {
          ...eventParamsWithType,
          page: responseData.page - 1,
        }
        await dispatch(getListEventPostsAction(prevPageParams)).unwrap()
      }
    } catch (error: any) {
      if (error?.message) {
        message.error({
          content: error.message,
        })
      }
    } finally {
      setIsSearching(false)
    }
  }

  const getListEventPostsActionLoading = useSelector((state: RootState) =>
    selectEventPostsLoading(state, 'getListEventPostsAction')
  )

  const onCloseConfirmModal = () => {
    setOpenConfirmDeleteModal(false)
  }

  const handleConfirmDelete = async () => {
    try {
      if (selectedEventId) {
        setIsModalLoading(true)

        const response = await dispatch(
          deleteEventPostAction(selectedEventId as string)
        ).unwrap()

        if (response?.success) {
          message.success(response.message)
          setIsModalLoading(false)
          setOpenConfirmDeleteModal(false)

          // Reload events after delete - stay on current page
          const params: {
            page: number
            limit: number
            status?: number | string
            type?: string
            search?: string
          } = {
            page: +pageEventPosts,
            limit: +limitEventPosts,
          }

          // Preserve event type filter
          if (eventType !== 'all') {
            params.type = eventType
          }

          // Preserve status filter
          if (status === 'published') {
            params.status = 1
          } else if (status === 'unpublished') {
            params.status = 0
          }

          // Preserve search filter
          if (debouncedValue !== null && debouncedValue.trim()) {
            params.search = debouncedValue.trim()
          }

          getEvents(params)
        }
      } else {
        setIsModalLoading(false)
        setOpenConfirmDeleteModal(false)
      }
    } catch (error: any) {
      console.log(error?.message)
      message.error(error?.message)
    }
  }

  const handleCreateEvent = () => {
    navigate(PATH_ADD_EVENT_NEWS)
  }

  const columns = [
    {
      width: '5%',
      title: enumTableTitleEvents.NO,
      dataIndex: 'index',
      key: 'index',
      ellipsis: true,
      align: 'center',
    },
    {
      width: '20%',
      title: () => {
        return (
          <div className="flex items-center justify-center w-full">
            <p className="m-0">{enumTableTitleEvents.TITLE}</p>
          </div>
        )
      },
      dataIndex: 'title',
      key: 'id',
      render: (e: string) => {
        return (
          <div className="flex items-center max-w-[460px]">
            <Typography.Text
              style={{
                width: 400,
                whiteSpace: 'nowrap',
              }}
              ellipsis={true}
            >
              {e}
            </Typography.Text>
          </div>
        )
      },
      ellipsis: true,
    },
    {
      width: '10%',
      title: () => {
        return (
          <div className="flex items-center justify-center w-full">
            <p className="m-0">{enumTableTitleEvents.TYPE}</p>
          </div>
        )
      },
      dataIndex: 'type',
      key: 'type',
      render: (e: string) => {
        const displayType =
          e === EEventPostType.EVENT_POST_NEWS
            ? EEventPostTypeLabel.EVENT_POST_NEWS
            : EEventPostTypeLabel.EVENT_POST_RECAP
        return (
          <div className="flex items-center justify-center">{displayType}</div>
        )
      },
      ellipsis: true,
    },
    {
      width: '15%',
      title: () => {
        return (
          <div className="flex items-center justify-center w-full">
            <p className="m-0">{enumTableTitleEvents.LOCATION}</p>
          </div>
        )
      },
      dataIndex: 'location',
      key: 'location',
      render: (e: string) => {
        return (
          <div className="flex items-center justify-center">
            <Typography.Text
              style={{
                width: 200,
                whiteSpace: 'nowrap',
              }}
              ellipsis={true}
            >
              {e || 'N/A'}
            </Typography.Text>
          </div>
        )
      },
      ellipsis: true,
    },
    {
      width: '12.5%',
      title: () => {
        return (
          <div className="flex items-center justify-center w-full">
            <p className="m-0">{enumTableTitleEvents.CREATE_TIME}</p>
            <div
              className="flex items-center ml-2 cursor-pointer"
              onClick={() => {
                setSort({
                  ...sort,
                  createdAt: sort.createdAt === 'asc' ? 'desc' : 'asc',
                })
                getEvents({
                  sort: {
                    ...sort,
                    createdAt: sort.createdAt === 'asc' ? 'desc' : 'asc',
                  },
                })
              }}
            >
              {sort.createdAt === 'asc' ? (
                <CaretDownOutlined />
              ) : (
                <CaretUpOutlined />
              )}
            </div>
          </div>
        )
      },
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (e: string) => {
        return (
          <div className="flex items-center justify-center">
            {e ? moment(e).format('MMMM DD, YYYY') : 'N/A'}
          </div>
        )
      },
      ellipsis: true,
    },
    {
      width: '5%',
      title: () => {
        return (
          <div className="flex items-center justify-center w-full">
            <p className="m-0">{enumTableTitleEvents.STATUS}</p>
          </div>
        )
      },
      dataIndex: 'status',
      key: 'status',
      render: (e: string) => {
        return (
          <div className="flex items-center justify-center capitalize">{e}</div>
        )
      },
      ellipsis: true,
    },
    {
      width: '7%',
      title: enumTableTitleEvents.ACTION,
      key: 'action',
      dataIndex: 'action',
      render: (_: any, record: { id: any }) => (
        <div className="flex items-center justify-center space-x-4">
          <Tooltip title={t('common:view_detail')}>
            <EditOutlined
              className="text-lg font-light mr-2.5 cursor-pointer text-[#184f64]"
              onClick={async () => {
                try {
                  await dispatch(getEventPostDetailAction(record.id)).unwrap()
                } catch (error: any) {
                  console.log(error?.message)
                }
                navigate(`${PATH_EVENT_POST}/event-detail/${record.id}`)
              }}
            />
          </Tooltip>
          <DeleteOutlined
            className={`text-lg font-light mr-2.5 cursor-pointer hover:opacity-80 !text-red-600`}
            onClick={() => {
              setOpenConfirmDeleteModal(true)
              setSelectedEventId(record.id)
            }}
          />
        </div>
      ),
      ellipsis: true,
      align: 'center',
    },
  ]

  const handleChangeStatus = (value: string) => {
    setStatus(value)
  }

  const handleChangeEventType = (value: string) => {
    setEventType(value)
  }

  // Separate effect for status and eventType filters - matches Workshop Events pattern
  useEffect(() => {
    const params: any = {
      page: 1,
      limit: 10,
    }

    // Add type filter
    if (eventType !== 'all') {
      params.type = eventType
    }

    // Add status filter
    if (status === 'published') {
      params.status = 1
    } else if (status === 'unpublished') {
      params.status = 0
    }

    // Add search if exists
    if (debouncedValue?.trim()) {
      params.search = debouncedValue.trim()
    }

    getEvents(params)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, eventType])

  // Separate effect for debounced search - matches Workshop Events pattern
  useEffect(() => {
    const params: any = {
      page: 1,
      limit: 10,
    }

    // Add search
    if (debouncedValue?.trim()) {
      params.search = debouncedValue.trim()
    }

    // Add type filter
    if (eventType !== 'all') {
      params.type = eventType
    }

    // Add status filter
    if (status === 'published') {
      params.status = 1
    } else if (status === 'unpublished') {
      params.status = 0
    }

    getEvents(params)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue])

  return (
    <EventManagementStyled>
      <div className="flex items-center justify-between py-[1.25rem] w-full">
        <div className="max-w-[900px] flex-1 flex gap-4">
          <Input
            prefix={<SearchOutlined className="pl-2" />}
            placeholder="Enter event title"
            value={searchValue === null ? '' : searchValue}
            maxLength={100}
            onChange={(e) => {
              setSearchValue(e.target.value)
            }}
            suffix={isSearching ? <Loading /> : undefined}
          />

          <div className="flex items-center justify-center gap-1 wrapper-type-filter">
            <div className="label-type">Type</div>
            <ShareSelectInput
              data={eventTypeOptions}
              value={eventType}
              style={{ width: '140px' }}
              onChange={handleChangeEventType}
              showArrow
            />
          </div>

          <div className="flex items-center justify-center gap-1 wrapper-status-filter">
            <div className="label-status">Status</div>
            <ShareSelectInput
              data={eventStatusOptions}
              value={status}
              style={{ width: '140px' }}
              onChange={handleChangeStatus}
              showArrow
            />
          </div>
        </div>
        <div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateEvent}
          >
            Create Event Post
          </Button>
        </div>
      </div>

      <div className="pb-[1.25rem]">
        {getListEventPostsActionLoading || isSearching ? (
          <Skeleton paragraph={{ rows: 4 }} />
        ) : (
          <SharedTable
            columns={columns as any}
            dataSource={eventPosts.map((item, index) => {
              return {
                index: index + 1,
                key: item?.id,
                id: item?.id,
                title: item?.title,
                type: item?.type,
                location: (item as any)?.location || 'N/A',
                createdAt: item.createdAt,
                status: item?.published ? 'Published' : 'Unpublished',
              }
            })}
            paginationProps={{
              total: !!totalEventPosts ? +totalEventPosts : undefined,
              pageSize: +limitEventPosts || 10,
              current: +pageEventPosts,
              onChange(page) {
                const params: {
                  page: number
                  status?: number | string
                  type?: string
                  search?: string
                } = {
                  page: page,
                }

                // Preserve search filter
                if (debouncedValue?.trim()) {
                  params.search = debouncedValue.trim()
                }

                // Preserve type filter
                if (eventType !== 'all') {
                  params.type = eventType
                }

                // Preserve status filter
                switch (status) {
                  case 'published':
                    params.status = 1
                    break
                  case 'unpublished':
                    params.status = 0
                    break
                  default:
                    break
                }

                getEvents(params)
              },
            }}
            sticky
          />
        )}
      </div>

      {openConfirmDeleteModal && (
        <ConfirmDeleteModal
          title="Delete Event Post "
          content="Are you sure to delete this event post?"
          open={openConfirmDeleteModal}
          isLoading={!!isModalLoading}
          onDelete={handleConfirmDelete}
          onClose={onCloseConfirmModal}
        />
      )}
    </EventManagementStyled>
  )
}

export default EventNewsPage

export { EventNewsPage }

const EventManagementStyled = styled('div')(() => {
  return {
    padding: '20px',
    borderRadius: '8px',
    backgroundColor: '#fff',

    '& .ant-table-wrapper': {
      margin: 0,
      '& .ant-table-content': {
        marginRight: 0,
      },
    },

    '.wrapper-type-filter, .wrapper-status-filter': {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',

      '.label-type, .label-status': {
        fontSize: '14px',
        fontWeight: '500',
        color: '#374151',
        minWidth: '40px',
      },
    },
  }
})
